import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { getMessages, sendMessage, updateChat } from '../services/api';
import { chatCache, storage } from '../utils/storage';
import { createMessageId, normalizeBackendResponse, sanitizeMessages } from '../utils/messageNormalizer';
import MessageBubble from './MessageBubble';
import TypingIndicator from './TypingIndicator';
import InlineNotice from './InlineNotice';
import toast from 'react-hot-toast';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCheck,
  faComments,
  faDatabase,
  faFileLines,
  faPaperPlane,
  faPenToSquare,
  faTable,
  faCircleNodes,
} from '@fortawesome/free-solid-svg-icons';

const getConnectionIcon = (type) => {
  const t = (type || '').toLowerCase();
  if (t === 'csv') return faFileLines;
  if (t === 'excel') return faTable;
  return faDatabase;
};

const SUGGESTIONS = [
  'Total sales by month',
  'Top customers',
  'Inventory by warehouse',
  'Revenue trends',
  'Employee count by department',
];

// A connection freshly returned from a CSV/Excel upload may carry the
// backend's dataset analysis directly on it (dataset_summary, warnings,
// suggested_questions, etc.). Reshaping — not authoring — these fields into
// a message envelope lets them render through the same MessageBubble
// renderer used for chat responses, so nothing is invented client-side.
const UPLOAD_SUMMARY_KEYS = ['dataset_summary', 'information', 'warnings', 'security', 'validation', 'suggested_questions', 'response_type', 'summary'];

function getUploadSummaryMessage(connection) {
  if (!connection || !UPLOAD_SUMMARY_KEYS.some(key => connection[key] !== undefined)) return null;
  return { ...connection, id: `upload-summary-${connection.id}`, role: 'assistant' };
}

export default function ChatPanel({ chat, connection, onChatRenamed }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [renamingTitle, setRenamingTitle] = useState(false);
  const [titleVal, setTitleVal] = useState('');
  const bottomRef = useRef(null);
  const inputRef = useRef(null);
  const textareaRef = useRef(null);

  const uploadSummaryMessage = useMemo(() => getUploadSummaryMessage(connection), [connection]);

  const scrollToBottom = useCallback(() => {
    setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: 'smooth' }), 80);
  }, []);

  const handleSuggest = useCallback((question) => {
    setInput(question);
    inputRef.current?.focus();
  }, []);

  // Load messages (from cache first, then API)
  useEffect(() => {
    if (!chat) { setMessages([]); return; }
    setTitleVal(chat.title);

    let alive = true;
    const chatId = chat.id;

    // Show cached instantly (just a placeholder — always superseded below)
    const cached = chatCache.get(chatId);
    if (cached.length) { setMessages(sanitizeMessages(cached)); scrollToBottom(); }

    // Then fetch fresh from API. This is the source of truth for this chat:
    // the fetched list always fully REPLACES current state (never merges
    // with cache or with in-flight optimistic messages), and a stale
    // response from a chat we've since navigated away from is ignored so it
    // can't clobber newer state or reintroduce already-superseded messages.
    setLoadingHistory(true);
    getMessages(chatId)
      .then(data => {
        if (!alive) return;
        const normalized = sanitizeMessages(data);
        setMessages(normalized);
        chatCache.set(chatId, normalized);
        scrollToBottom();
      })
      .catch(() => { if (alive) toast.error('Could not load messages'); })
      .finally(() => { if (alive) setLoadingHistory(false); });

    return () => { alive = false; };
  }, [chat?.id, scrollToBottom]);

  const handleSend = async () => {
    const text = input.trim();
    if (!text || loading) return;
    setInput('');
    if (textareaRef.current) textareaRef.current.style.height = 'auto';

    // Optimistic user message; the "Thinking..." indicator (below) covers
    // the assistant side instead of a synthetic placeholder message.
    const tempUser = {
      id: createMessageId('user'), chat_id: chat.id, role: 'user',
      content: text, created_at: new Date().toISOString(),
    };
    setMessages(prev => [...prev, tempUser]);
    setLoading(true);
    scrollToBottom();

    try {
      const result = await sendMessage(chat.id, text);
      setMessages(prev => {
        const finalMessages = normalizeBackendResponse(result);
        const finalUser = finalMessages.find(m => m?.role === 'user') || null;
        const finalAssistant = finalMessages.find(m => m?.role === 'assistant') || null;
        // Swap the optimistic user placeholder for the server-confirmed
        // message in place; append the assistant reply once it arrives.
        const updated = prev.flatMap(m => (m.id === tempUser.id ? [finalUser || m] : [m]));
        const withAssistant = finalAssistant ? [...updated, finalAssistant] : updated;
        const sanitized = sanitizeMessages(withAssistant);
        chatCache.set(chat.id, sanitized);
        return sanitized;
      });

      // Auto-rename chat after first message
      if (messages.length === 0 && chat.title === 'New Chat') {
        const newTitle = text.slice(0, 48);
        updateChat(chat.id, { title: newTitle }).then(updated => {
          onChatRenamed && onChatRenamed(updated);
        }).catch(() => {});
      }
    } catch (err) {
      toast.error(err.message || 'Failed to send message');
      setMessages(prev => prev.filter(m => m.id !== tempUser.id));
    }
    setLoading(false);
    scrollToBottom();
    inputRef.current?.focus();
  };

  const handlePaginate = async (query, page) => {
    if (!chat || loading || !query) return;
    setLoading(true);
    try {
      const result = await sendMessage(chat.id, `Show page ${page + 1}`, { page, query });
      setMessages(prev => {
        const appended = normalizeBackendResponse(result);
        const updated = sanitizeMessages([...prev, ...appended]);
        chatCache.set(chat.id, updated);
        return updated;
      });
      scrollToBottom();
    } catch (err) {
      toast.error(err.message || 'Failed to load page');
    } finally {
      setLoading(false);
    }
  };

  const handleKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  const autoResize = (e) => {
    e.target.style.height = 'auto';
    e.target.style.height = Math.min(e.target.scrollHeight, 140) + 'px';
    setInput(e.target.value);
  };

  const handleRenameSubmit = async (e) => {
    e.preventDefault();
    if (!titleVal.trim()) return;
    try {
      const updated = await updateChat(chat.id, { title: titleVal.trim() });
      onChatRenamed && onChatRenamed(updated);
      setRenamingTitle(false);
      toast.success('Renamed');
    } catch { toast.error('Failed to rename'); }
  };

  if (!chat) return <EmptyState connection={connection} />;

  return (
    <div style={s.panel}>
      {/* Chat Header */}
      <div style={s.header}>
        <div style={s.headerLeft}>
          <span style={s.connTag}>
            <FontAwesomeIcon icon={getConnectionIcon(connection?.type)} aria-hidden="true" />
            {connection?.name}
          </span>
          {renamingTitle ? (
            <form onSubmit={handleRenameSubmit} style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <input
                style={s.renameInput} value={titleVal} onChange={e => setTitleVal(e.target.value)}
                autoFocus onBlur={() => setRenamingTitle(false)}
              />
              <button type="submit" style={s.renameBtn} aria-label="Confirm rename">
                <FontAwesomeIcon icon={faCheck} aria-hidden="true" />
              </button>
            </form>
          ) : (
            <h2 style={s.chatTitle} onClick={() => setRenamingTitle(true)} title="Click to rename">
              {chat.title}
              <span style={s.editHint}><FontAwesomeIcon icon={faPenToSquare} aria-hidden="true" /></span>
            </h2>
          )}
        </div>
        <div style={s.headerMeta}>
          <span style={s.msgCount}>{messages.length} messages</span>
        </div>
      </div>

      {connection?.file_is_unreliable && (
        <div style={s.warningBanner}>
          <InlineNotice variant="warning" text="This file has inconsistent formatting (some rows have different column counts). Results may be inaccurate. Please clean the file for better accuracy." />
        </div>
      )}

      {/* Messages */}
      <div style={s.messages}>
        {loadingHistory && messages.length === 0 && (
          <div style={s.loadingHistory}>
            <span style={s.spinner} />
            Loading conversation…
          </div>
        )}

        {messages.length === 0 && !loadingHistory && (
          uploadSummaryMessage ? (
            <MessageBubble message={uploadSummaryMessage} isLast onSuggest={handleSuggest} />
          ) : (
            <div style={s.welcome}>
              <div style={s.welcomeWordmark}>Datasphere</div>
              <p style={s.welcomeSub}>Connect your data and ask questions in natural language.</p>
              <div style={s.suggestions}>
                {SUGGESTIONS.map((sug, i) => (
                  <button key={i} onClick={() => handleSuggest(sug)} style={s.suggestion}>
                    {sug}
                  </button>
                ))}
              </div>
            </div>
          )
        )}

        {messages.filter(Boolean).map((msg, i) => (
          <MessageBubble
            key={msg.id}
            message={msg}
            isLast={i === messages.filter(Boolean).length - 1}
            onPaginate={handlePaginate}
            onSuggest={handleSuggest}
          />
        ))}

        {loading && <TypingIndicator />}

        <div ref={bottomRef} />
      </div>

      {/* Input */}
      <div style={s.inputWrap}>
        <div style={s.inputBox}>
          <textarea
            ref={r => { inputRef.current = r; textareaRef.current = r; }}
            style={s.textarea}
            placeholder={`Ask about ${connection?.name || 'your data'}…`}
            value={input}
            onChange={autoResize}
            onKeyDown={handleKey}
            rows={1}
            disabled={loading}
          />
          <button
            onClick={handleSend}
            disabled={!input.trim() || loading}
            style={{ ...s.sendBtn, ...((!input.trim() || loading) ? s.sendBtnDisabled : {}) }}
            aria-label="Send message"
          >
            {loading ? <span style={s.miniSpin} /> : <FontAwesomeIcon icon={faPaperPlane} aria-hidden="true" />}
          </button>
        </div>
        <p style={s.hint}>Press Enter to send · Shift+Enter for new line</p>
      </div>
    </div>
  );
}

function EmptyState({ connection }) {
  return (
    <div style={s.emptyState}>
      {connection ? (
        <>
          <FontAwesomeIcon icon={faComments} style={{ fontSize: 40 }} aria-hidden="true" />
          <h3 style={s.emptyTitle}>Start a conversation</h3>
          <p style={s.emptySub}>Select a chat from the sidebar or create a new one to start querying <strong>{connection.name}</strong>.</p>
        </>
      ) : (
        <>
          <FontAwesomeIcon icon={faCircleNodes} style={{ fontSize: 40 }} aria-hidden="true" />
          <h3 style={s.emptyTitle}>Connect a data source</h3>
          <p style={s.emptySub}>Click "New Connection" in the sidebar to connect PostgreSQL, CSV, or Excel and start asking questions.</p>
        </>
      )}
    </div>
  );
}

const s = {
  panel: {
    flex: 1, display: 'flex', flexDirection: 'column', height: '100vh',
    background: 'var(--bg-primary)', overflow: 'hidden',
  },
  header: {
    padding: '14px 24px', background: 'var(--bg-primary)', borderBottom: '1px solid var(--border-subtle)',
    display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0,
    animation: 'fadeIn 0.25s ease',
  },
  headerLeft: { display: 'flex', flexDirection: 'column', gap: 4 },
  connTag: {
    display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11,
    color: 'var(--text-muted)', fontWeight: 600, letterSpacing: '0.3px',
  },
  chatTitle: {
    fontSize: 17, fontWeight: 700, color: 'var(--text-primary)', cursor: 'pointer',
    display: 'flex', alignItems: 'center', gap: 8,
  },
  editHint: { fontSize: 12, opacity: 0, transition: 'opacity 0.2s' },
  renameInput: {
    padding: '4px 10px', borderRadius: 8, border: '1.5px solid var(--accent)',
    background: 'var(--bg-primary)', color: 'var(--text-primary)', fontSize: 16,
    fontWeight: 700, outline: 'none', fontFamily: 'var(--font-main)',
  },
  renameBtn: {
    padding: '4px 10px', borderRadius: 8, background: 'var(--accent)',
    color: '#fff', border: 'none', cursor: 'pointer', fontWeight: 700,
  },
  headerMeta: { display: 'flex', alignItems: 'center', gap: 12 },
  msgCount: { fontSize: 11, color: 'var(--text-muted)' },
  warningBanner: { margin: '14px 24px 0' },
  messages: {
    flex: 1, overflowY: 'auto', padding: '24px', display: 'flex',
    flexDirection: 'column', maxWidth: 860, width: '100%', marginInline: 'auto',
  },
  loadingHistory: {
    display: 'flex', alignItems: 'center', gap: 10, color: 'var(--text-muted)',
    fontSize: 13, justifyContent: 'center', padding: '40px 0',
  },
  spinner: {
    width: 16, height: 16, border: '2px solid var(--border-subtle)',
    borderTopColor: 'var(--accent)', borderRadius: '50%',
    animation: 'spin 0.7s linear infinite', display: 'inline-block',
  },
  welcome: {
    flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center',
    justifyContent: 'center', padding: '40px 20px', textAlign: 'center',
    animation: 'fadeIn 0.4s ease',
  },
  welcomeWordmark: { fontSize: 28, fontWeight: 800, color: 'var(--text-primary)', letterSpacing: '-0.02em', marginBottom: 10 },
  welcomeSub: { fontSize: 14, color: 'var(--text-muted)', marginBottom: 28, maxWidth: 420, lineHeight: 1.6 },
  suggestions: { display: 'flex', flexWrap: 'wrap', gap: 8, width: '100%', maxWidth: 520, justifyContent: 'center' },
  suggestion: {
    padding: '9px 16px', borderRadius: 999, border: '1px solid var(--border-subtle)',
    background: 'transparent', color: 'var(--text-secondary)', fontSize: 13,
    cursor: 'pointer', textAlign: 'left', transition: 'all 0.2s', fontFamily: 'var(--font-main)',
  },
  inputWrap: {
    padding: '16px 24px 20px', background: 'var(--bg-primary)',
    flexShrink: 0,
  },
  inputBox: {
    display: 'flex', gap: 10, alignItems: 'flex-end', maxWidth: 860, marginInline: 'auto',
    background: 'var(--bg-secondary)', borderRadius: 16,
    border: '1px solid var(--border-subtle)', padding: '10px 12px',
    transition: 'border-color 0.2s',
  },
  textarea: {
    flex: 1, background: 'none', border: 'none', outline: 'none', resize: 'none',
    fontSize: 14, color: 'var(--text-primary)', lineHeight: 1.6,
    fontFamily: 'var(--font-main)', maxHeight: 140, overflowY: 'auto',
  },
  sendBtn: {
    width: 36, height: 36, borderRadius: 10, background: 'var(--accent)',
    color: '#fff', border: 'none', cursor: 'pointer', fontSize: 18, fontWeight: 700,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    transition: 'all 0.2s', flexShrink: 0,
  },
  sendBtnDisabled: { opacity: 0.4, cursor: 'not-allowed' },
  miniSpin: {
    width: 14, height: 14, border: '2px solid rgba(255,255,255,0.4)',
    borderTopColor: '#fff', borderRadius: '50%', animation: 'spin 0.7s linear infinite',
    display: 'inline-block',
  },
  hint: { fontSize: 11, color: 'var(--text-muted)', textAlign: 'center', marginTop: 8 },
  emptyState: {
    flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center',
    justifyContent: 'center', padding: 40, textAlign: 'center',
    animation: 'fadeIn 0.4s ease',
  },
  emptyTitle: { fontSize: 20, fontWeight: 700, color: 'var(--text-primary)', marginTop: 16, marginBottom: 8 },
  emptySub: { fontSize: 14, color: 'var(--text-muted)', maxWidth: 360, lineHeight: 1.6 },
};
